import { useState } from "react";
import { Search } from "lucide-react";

import { cn } from "@/lib/utils";
import { Avatar } from "@/components/ui/avatar";
import type { Conversation } from "../api/threads";

interface ConversationsPanelProps {
  conversations: Conversation[];
  selectedId: string | null;
  onSelect: (key: string) => void;
  label: string;
  className?: string;
}

// Composite key: plain contactId, unless the conversation carries a loanerId (account
// representing multiple companies), in which case the two combine. Same pattern as
// DashboardLayout.
const convKey = (c: { loanerId?: number | null; contactId: number }) =>
  c.loanerId != null ? `${c.loanerId}:${c.contactId}` : `${c.contactId}`;

export function ConversationsPanel({
  conversations,
  selectedId,
  onSelect,
  label,
  className,
}: ConversationsPanelProps) {
  const [query, setQuery] = useState("");
  const filtered = conversations.filter((c) =>
    c.name.toLowerCase().includes(query.trim().toLowerCase())
  );
  // Conversations with a message bubble to the top (already sorted newest-first upstream);
  // everyone else stays in the plain contact list below.
  const active = filtered.filter((c) => c.lastMessageAt);
  const rest = filtered.filter((c) => !c.lastMessageAt);
  // Multi-company gate: only group by company when more than one distinct loanerId is
  // present. Single-company loaners and all investor views render exactly as before.
  const companies = new Set(filtered.filter((c) => c.loanerId != null).map((c) => c.loanerId));
  const grouped = companies.size > 1;

  return (
    <aside
      className={cn(
        // Mobile: fill the space left of the icon rail (flex-1). md+: fixed 320px column.
        "min-w-0 flex-1 flex-col border-r border-white/5 md:w-[320px] md:flex-none",
        className
      )}
    >
      <div className="px-4 pb-3 pt-5">
        <h1 className="mb-4 text-xl font-bold">Chat</h1>
        <div className="relative">
          <Search className="pointer-events-none absolute left-4 top-1/2 size-4 -translate-y-1/2 text-white/40" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Søk i samtaler"
            aria-label="Søk i samtaler"
            className="h-11 w-full rounded-xl border border-white/10 bg-white/[0.04] pl-10 pr-3 text-sm text-white placeholder:text-white/40 focus:border-teal-400/50 focus:outline-none"
          />
        </div>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto px-2 pb-2">
        {filtered.length === 0 && (
          <p className="px-2 py-8 text-center text-sm text-white/40">
            Ingen kontakter funnet.
          </p>
        )}

        {/* Active conversations first, newest at the top (sorted upstream by lastMessageAt). */}
        {active.length > 0 && (
          <>
            <SectionLabel>Samtaler</SectionLabel>
            {grouped ? (
              groupByCompany(active).map((g) => (
                <div key={`active-${g.loanerId ?? "none"}`}>
                  <SectionLabel>{g.companyLabel}</SectionLabel>
                  <ConversationList items={g.items} selectedId={selectedId} onSelect={onSelect} />
                </div>
              ))
            ) : (
              <ConversationList items={active} selectedId={selectedId} onSelect={onSelect} />
            )}
          </>
        )}

        {/* All remaining contacts (no conversation yet), alphabetical. */}
        {rest.length > 0 && (
          <>
            <SectionLabel>{label}</SectionLabel>
            {grouped ? (
              groupByCompany(rest).map((g) => (
                <div key={`rest-${g.loanerId ?? "none"}`}>
                  <SectionLabel>{g.companyLabel}</SectionLabel>
                  <ConversationList items={g.items} selectedId={selectedId} onSelect={onSelect} />
                </div>
              ))
            ) : (
              <ConversationList items={rest} selectedId={selectedId} onSelect={onSelect} />
            )}
          </>
        )}
      </div>
    </aside>
  );
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <p className="px-3 pb-2 pt-3 text-xs font-semibold uppercase tracking-wider text-white/40">
      {children}
    </p>
  );
}

/** Groups conversations by loanerId, preserving each group's incoming order (already
 *  sorted upstream). Only used when more than one company is present (see `grouped`). */
function groupByCompany(items: Conversation[]) {
  const order: (number | null)[] = [];
  const byLoanerId = new Map<number | null, Conversation[]>();
  for (const c of items) {
    const k = c.loanerId ?? null;
    if (!byLoanerId.has(k)) {
      byLoanerId.set(k, []);
      order.push(k);
    }
    byLoanerId.get(k)!.push(c);
  }
  return order.map((loanerId) => {
    const group = byLoanerId.get(loanerId)!;
    return {
      loanerId,
      companyLabel: group[0].company ?? (loanerId != null ? `Selskap #${loanerId}` : ""),
      items: group,
    };
  });
}

function ConversationList({
  items,
  selectedId,
  onSelect,
}: {
  items: Conversation[];
  selectedId: string | null;
  onSelect: (key: string) => void;
}) {
  return (
    <ul className="space-y-0.5">
      {items.map((c) => {
        const key = convKey(c);
        return (
          <li key={key}>
            <ConversationRow
              conversation={c}
              selected={key === selectedId}
              onClick={() => onSelect(key)}
            />
          </li>
        );
      })}
    </ul>
  );
}

function ConversationRow({
  conversation,
  selected,
  onClick,
}: {
  conversation: Conversation;
  selected: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-current={selected}
      className={cn(
        "flex w-full items-center gap-3 rounded-xl px-3 py-3 text-left transition-colors hover:bg-white/[0.04]",
        selected &&
          "bg-teal-500/10 ring-1 ring-inset ring-teal-500/20 hover:bg-teal-500/10"
      )}
    >
      <Avatar initials={conversation.initials} className="size-12 text-base" />
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline justify-between gap-2">
          <span className="truncate text-[15px] font-semibold text-white">
            {conversation.name}
          </span>
          <span className="shrink-0 text-xs text-white/40">{conversation.timeLabel}</span>
        </div>
        {conversation.unverified && (
          <span className="mt-0.5 inline-flex items-center rounded-full bg-amber-500/15 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-300 ring-1 ring-inset ring-amber-500/30">
            Ikke verifisert bruker
          </span>
        )}
        <div className="mt-0.5 flex items-center justify-between gap-2">
          <span
            className={cn(
              "truncate text-sm",
              conversation.unread > 0
                ? "font-medium text-white"
                : conversation.preview
                  ? "text-white/55"
                  : "italic text-white/30"
            )}
          >
            {conversation.preview || "Ingen meldinger ennå"}
          </span>
          {conversation.unread > 0 && (
            <span className="flex size-5 shrink-0 items-center justify-center rounded-full bg-teal-400 text-[11px] font-semibold text-[#04231d]">
              {conversation.unread}
            </span>
          )}
        </div>
      </div>
    </button>
  );
}
